import { useMemo, JSX } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import {
  StructuredEvacuationsEditLayer,
  STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS,
  STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS,
} from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import {
  EvacZone,
  FormEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { Flooding } from 'features/flooding/flooding.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { getEvacZoneStylesFromRegions } from 'shared/utils';
import { REGION_EVAC_ZONES_REFETCH_INTERVAL } from '../../constants';
import { prepareZonesForMapLayers, ZonesForMapLayers } from '../../utils';
import {
  useHandleEvacZoneClick,
  UseHandleEvacZoneClickReturn,
} from '../useHandleEvacZoneClick';
import {
  UseEvacZonesChangedNotification,
  useEvacZonesChangedNotification,
  useRegionEvacZones,
} from '../useRegionEvacZones';

export type UseGeoEventEvacZonesParams = {
  regions: GeoEventRegion[];
  evacZoneStatuses: FormEvacZoneStatus[];
  geoEvent?: Wildfire | Flooding;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  temporarilyDisplayEvacZones?: boolean;
  releasePendingZoneUids?: string[];
  /**
   * When true and geoEvent is undefined (creation mode), ensures evac zone
   * data is fetched and kept fresh via refetching.
   */
  forceRegionEvacZonesFetch?: boolean;
};

export type UseGeoEventEvacZonesReturn = UseEvacZonesChangedNotification &
  ZonesForMapLayers &
  UseEvacZonesChangedNotification & {
    renderMapLayers: (disableClicks?: boolean) => JSX.Element | null;
    // renamed from useRegionEvacZones
    isLoadingEvacZones: boolean;
    isErrorEvacZones: boolean;
    regionEvacZones: EvacZone[];
    // from getEvacZoneStylesFromRegions
    evacZoneStyles: RegionEvacZoneStyle[];
    // from useHandleEvacZoneClick
    handleEvacZoneClick: UseHandleEvacZoneClickReturn;
  };

export const useGeoEventEvacZones = (
  params: UseGeoEventEvacZonesParams,
): UseGeoEventEvacZonesReturn => {
  const {
    regions,
    evacZoneStatuses,
    geoEvent,
    setValue,
    temporarilyDisplayEvacZones,
    releasePendingZoneUids,
    forceRegionEvacZonesFetch = false,
  } = params;

  const {
    onZoneStateChanged,
    lockEvacuationZonesChangedNotification,
    unlockEvacuationZonesChangedNotification,
  } = useEvacZonesChangedNotification({ geoEventId: geoEvent?.id });

  /**
   * Normalize evacuation zone statuses for consistent downstream processing.
   * We need a valid empty fallback for useRegionEvacZones to determine if zones
   * are locked due to another incident, so we can't return undefined when
   * geoEvent is not provided.
   */
  const effectiveEvacZoneStatuses = useMemo(
    () => geoEvent?.evacZoneStatuses ?? [],
    [geoEvent],
  );

  const shouldFetchEvacZones = Boolean(geoEvent || forceRegionEvacZonesFetch);

  const {
    regionEvacZones,
    isLoading: isLoadingEvacZones,
    isError: isErrorEvacZones,
  } = useRegionEvacZones({
    regions,
    evacZoneStatuses: effectiveEvacZoneStatuses,
    refetchInterval: shouldFetchEvacZones
      ? REGION_EVAC_ZONES_REFETCH_INTERVAL
      : undefined,
    onZoneStateChanged,
    enabled: shouldFetchEvacZones,
  });

  const evacZoneStyles = useMemo(() => {
    return getEvacZoneStylesFromRegions(regions);
  }, [regions]);

  const { evacZones, pendingEvacZones } = useMemo(() => {
    return prepareZonesForMapLayers(
      regionEvacZones,
      evacZoneStatuses,
      releasePendingZoneUids,
    );
  }, [regionEvacZones, evacZoneStatuses, releasePendingZoneUids]);

  const handleEvacZoneClick = useHandleEvacZoneClick({
    regionEvacZones,
    evacZoneStatuses,
    geoEvent,
    setValue: (_name: string, value: FormEvacZoneStatus[]) =>
      setValue('evacZoneStatuses', value, { shouldDirty: true }),
  });

  const renderMapLayers = (disableClicks?: boolean): JSX.Element | null => {
    return (
      <>
        <StructuredEvacuationsEditLayer
          {...STRUCTURED_EVAC_EDIT_LAYER_DEFAULT_PROPS}
          visible
          evacZones={evacZones}
          onClick={
            temporarilyDisplayEvacZones || disableClicks
              ? undefined
              : handleEvacZoneClick
          }
        />
        <StructuredEvacuationsEditLayer
          {...STRUCTURED_EVAC_PENDING_LAYER_DEFAULT_PROPS}
          visible
          evacZones={pendingEvacZones}
        />
      </>
    );
  };

  return {
    regionEvacZones,
    evacZoneStyles,
    isLoadingEvacZones,
    isErrorEvacZones,
    handleEvacZoneClick,
    renderMapLayers,
    // ZonesForMapLayers
    evacZones,
    pendingEvacZones,
    // UseHandleEvacZoneClickReturn
    onZoneStateChanged,
    lockEvacuationZonesChangedNotification,
    unlockEvacuationZonesChangedNotification,
  };
};
